import React, { useEffect, useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Line,
  ComposedChart,
} from "recharts";
import { api, scopeParams } from "../lib/api";
import { useSession } from "../context/SessionContext";
import KpiCard from "../components/KpiCard";
import ModuleHero from "../components/ModuleHero";
import { StatusPill } from "../components/HealthPill";
import SizedChart from "../components/SizedChart";
import ExportButton from "../components/ExportButton";
import { downloadCSV, stamp } from "../lib/csv";
import { fmtInt, fmtPct, initials } from "../lib/format";

const HR = () => {
  const { session, focusBranchId } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/hr", { params: scopeParams(session, focusBranchId) })
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [session, focusBranchId]);

  const roster = useMemo(() => {
    if (!data) return [];
    if (statusFilter === "all") return data.roster;
    if (statusFilter === "present") return data.roster.filter((r) => r.status === "Present");
    if (statusFilter === "absent") return data.roster.filter((r) => r.status !== "Present");
    return data.roster;
  }, [data, statusFilter]);

  const exportRosterCSV = () => {
    if (!roster.length) return;
    downloadCSV(
      `angadya-roster-${stamp()}.csv`,
      [
        { key: "staff_id", label: "Staff ID" },
        { key: "name", label: "Name" },
        { key: "role", label: "Role" },
        { key: "branch_name", label: "Branch" },
        { key: "region", label: "Region" },
        { key: "shift", label: "Shift" },
        { key: "status", label: "Status" },
      ],
      roster
    );
  };

  const exportBranchCoverageCSV = () => {
    if (!data?.branch_summary?.length) return;
    downloadCSV(
      `angadya-coverage-${stamp()}.csv`,
      [
        { key: "branch_name", label: "Branch" },
        { key: "region", label: "Region" },
        { key: "headcount", label: "Headcount" },
        { key: "present", label: "Present" },
        { key: "absent", label: "Absent" },
        { key: "peak_gap_pct", label: "Peak Gap %" },
        { key: "flag", label: "Status" },
      ],
      data.branch_summary
    );
  };

  return (
    <div className="space-y-6" data-testid="hr-page">
      {!loading && data?.kpis && (
        <ModuleHero
          testId="hr-hero"
          eyebrow="Coverage · Today"
          title="Roster health, right now"
          subtitle="Present vs. total headcount across every branch — with a live count of shifts running short of peak-hour demand."
          primary={{
            label: "Present today",
            value: data.kpis.present_today || 0,
            countFormatter: (n) => `${n} / ${data.kpis.total_headcount}`,
          }}
          secondaries={[
            {
              label: "Coverage",
              value: `${Math.round((data.kpis.present_today / Math.max(1, data.kpis.total_headcount)) * 100)}%`,
              accent: (data.kpis.present_today / Math.max(1, data.kpis.total_headcount)) >= 0.85 ? "green" : "amber",
            },
            {
              label: "Understaffed",
              value: data.kpis.understaffed_branches,
              accent: data.kpis.understaffed_branches > 0 ? "red" : "green",
            },
          ]}
          accent={data.kpis.understaffed_branches > 0 ? "amber" : "teal"}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KpiCard testId="hr-kpi-headcount" label="Total headcount" countTarget={loading ? 0 : (data?.kpis.total_headcount || 0)} hint="Across scope" />
        <KpiCard testId="hr-kpi-present" label="Present today" countTarget={loading ? 0 : (data?.kpis.present_today || 0)} tone="success" />
        <KpiCard testId="hr-kpi-leave" label="On leave" countTarget={loading ? 0 : (data?.kpis.on_leave || 0)} tone="warn" />
        <KpiCard testId="hr-kpi-understaffed" label="Understaffed branches" countTarget={loading ? 0 : (data?.kpis.understaffed_branches || 0)} tone="danger" hint="Peak-hour gap > 15%" />
      </div>

      {/* Headcount vs footfall chart */}
      <section className="ang-panel">
        <header className="border-b border-ang px-6 py-4">
          <div className="text-[10px] uppercase tracking-widest text-ang-muted">Headcount vs. Footfall · Today</div>
          <h3 className="font-serif text-xl mt-1">Where the coverage gap sits</h3>
        </header>
        <SizedChart height={280}>
            <ComposedChart data={data?.hourly || []} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
              <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} axisLine={false} tickLine={false} />
              <YAxis yAxisId="l" axisLine={false} tickLine={false} />
              <YAxis yAxisId="r" orientation="right" axisLine={false} tickLine={false} />
              <Tooltip labelFormatter={(h) => `${h}:00`} />
              <Bar yAxisId="l" dataKey="footfall" fill="#14142B" name="Footfall" />
              <Line yAxisId="r" type="monotone" dataKey="staff_on_floor" stroke="#C9A15C" strokeWidth={2} dot={false} name="Staff on floor" />
            </ComposedChart>
          </SizedChart>
      </section>

      {/* Branch summary */}
      <section className="ang-panel">
        <header className="border-b border-ang px-6 py-4 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Branch coverage</div>
            <h3 className="font-serif text-xl mt-1">Peak-hour staffing gap · sorted worst first</h3>
          </div>
          <ExportButton
            label="Export CSV"
            testId="btn-export-csv-coverage"
            onClick={exportBranchCoverageCSV}
            disabled={loading || !data?.branch_summary?.length}
          />
        </header>
        <table className="w-full text-sm" data-testid="branch-summary-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b border-ang">
              <th className="text-left px-6 py-3">Branch</th>
              <th className="text-right px-6 py-3 w-32">Headcount</th>
              <th className="text-right px-6 py-3 w-28">Present</th>
              <th className="text-right px-6 py-3 w-28">Absent</th>
              <th className="text-right px-6 py-3 w-32">Peak Gap</th>
              <th className="text-center px-6 py-3 w-40">Status</th>
            </tr>
          </thead>
          <tbody>
            {(data?.branch_summary || []).map((b, i) => (
              <tr key={b.branch_id} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
                <td className="px-6 py-2.5">
                  <div className="font-serif">{b.branch_name}</div>
                  <div className="text-[11px] uppercase tracking-widest text-ang-muted">{b.region} Chennai</div>
                </td>
                <td className="px-6 py-2.5 text-right tabular-nums">{b.headcount}</td>
                <td className="px-6 py-2.5 text-right tabular-nums">{b.present}</td>
                <td className="px-6 py-2.5 text-right tabular-nums">{b.absent}</td>
                <td className="px-6 py-2.5 text-right tabular-nums">{fmtPct(b.peak_gap_pct)}</td>
                <td className="px-6 py-2.5 text-center">
                  <StatusPill band={b.flag === "understaffed" ? "red" : b.flag === "balanced" ? "green" : "amber"}>{b.flag}</StatusPill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Roster */}
      <section className="ang-panel">
        <header className="flex items-center justify-between border-b border-ang px-6 py-4 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Roster</div>
            <h3 className="font-serif text-xl mt-1">Attendance today · {data?.roster_total || 0} staff</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1" data-testid="roster-filter">
              {[{id:"all",l:"All"},{id:"present",l:"Present"},{id:"absent",l:"Absent / Leave"}].map((f) => (
                <button
                  key={f.id}
                  data-testid={`roster-filter-${f.id}`}
                  onClick={() => setStatusFilter(f.id)}
                  className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                    statusFilter === f.id ? "bg-[#14142B] text-white border-[#14142B]" : "bg-white border-ang hover:border-[#14142B]"
                  }`}
                >
                  {f.l}
                </button>
              ))}
            </div>
            <ExportButton
              label="Export CSV"
              testId="btn-export-csv-roster"
              onClick={exportRosterCSV}
              disabled={loading || !roster.length}
            />
          </div>
        </header>
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-sm" data-testid="roster-table">
            <thead className="sticky top-0 bg-white">
              <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
                <th className="text-left px-4 py-3 w-14"></th>
                <th className="text-left px-4 py-3">Staff</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Branch</th>
                <th className="text-left px-4 py-3 w-24">Shift</th>
                <th className="text-center px-4 py-3 w-32">Status</th>
              </tr>
            </thead>
            <tbody>
              {roster.slice(0, 300).map((r, i) => (
                <tr key={r.staff_id} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
                  <td className="px-4 py-2.5">
                    <div className="w-8 h-8 flex items-center justify-center text-[10px] font-semibold" style={{ background: "#14142B", color: "#C9A15C" }}>
                      {initials(r.name)}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-ang-muted">{r.staff_id}</div>
                  </td>
                  <td className="px-4 py-2.5">{r.role}</td>
                  <td className="px-4 py-2.5">
                    <div>{r.branch_name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-ang-muted">{r.region}</div>
                  </td>
                  <td className="px-4 py-2.5">{r.shift}</td>
                  <td className="px-4 py-2.5 text-center">
                    <StatusPill band={r.status === "Present" ? "green" : r.status === "On Leave" ? "amber" : "red"}>{r.status}</StatusPill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default HR;
